import { Popover } from "@base-ui-components/react/popover"
import { Tooltip } from "@base-ui-components/react/tooltip"
import NiceModal from "@ebay/nice-modal-react"
import cx from "classnames"
import { CircleAlert, GitFork, GitPullRequest, Loader2, PanelRightClose, PanelRightOpen, Plug, Plus, Server, ShieldCheck } from "lucide-react"
import { observer } from "mobx-react"
import { Fragment, type ReactElement, type ReactNode, useEffect, useState } from "react"
import { Z_INDEX } from "../constants"
import { selectDirectory } from "../electronAPIs/shell"
import { useMediaQuery } from "../hooks/useMediaQuery"
import { usePortalContainer } from "../hooks/usePortalContainer"
import { TRAY_CONFIGS } from "../modules/registry"
import type { TrayConfig } from "../modules/types"
import { nestClient } from "../nest/client"
import type { GitSummaryResponse } from "../nestAPIs/git"
import type { TaskModel } from "../store/TaskModel"
import { useOptionalCodeStore } from "../store/context"
import type { TrayManager, TrayType } from "../store/managers/TrayManager"
import type { TaskTimelineNavigationTarget } from "../timeline/taskTimelineTypes"
import { pullRequestAutomationStatus } from "../utils/pullRequestAutomation"
import { requestWildLarkMethod, type WildLarkExternalWatchStatusResult, type WildLarkPullRequestSnapshot } from "../wildlark/client"
import { CopyMarkdownButton } from "./CopyMarkdownButton"
import {
    type ExternalWatchPresentationStatus,
    LinkedRequestControl,
    TaskDirectorySelector,
    TaskGitControl,
    type TaskPullRequestControl,
    type TaskWorktreeControl,
} from "./TaskGitControl"
import { TaskSessionInfoButton } from "./TaskSessionInfoButton"
import { TaskMcpSelector } from "./mcp/TaskMcpSelector"
import { ProcessesModal } from "./processes/ProcessesModal"
import { AppToolsControl } from "./sessions/AppToolsControl"
import { TaskTimelineRail } from "./thread/TaskTimelineRail"
import { CompactSheet } from "./tray/CompactSheet"
import { TrayBadge } from "./tray/TrayBadge"

const CHECKOUT_SCOPED_TRAYS = new Set<TrayType>(["gitlog", "files", "search", "terminal"])

interface TaskWorkspaceSidebarProps {
    taskId: string
    workspaceId: string
    tray: TrayManager
    taskModel: TaskModel
    gitStatus?: GitSummaryResponse | null
    pullRequest?: TaskPullRequestControl
    externalWatchStatus?: ExternalWatchPresentationStatus
    pullRequestSnapshot?: WildLarkPullRequestSnapshot
    worktree?: TaskWorktreeControl
    onDirectoryChange?: (path: string | null) => Promise<string | null>
    onCheckoutSummariesOpen?: () => void
    showTestFiles?: boolean
    enabledMcpServerIds?: string[]
    onMcpServerIdsChange?: (serverIds: string[]) => void
    mobileOpen: boolean
    onMobileClose: () => void
    hideWorkspaceTools?: boolean
    desktopContained?: boolean
    onExpandedChange?: (expanded: boolean) => void
    onTimelineNavigate?: (target: TaskTimelineNavigationTarget) => void
}

function SidebarSection({
    label,
    expanded,
    meta,
    flush = false,
    fill = false,
    children,
}: {
    label: string
    expanded: boolean
    meta?: ReactNode
    flush?: boolean
    fill?: boolean
    children: ReactNode
}): JSX.Element {
    return (
        <section className={cx("border-b border-border py-2 last:border-b-0", fill && "flex min-h-0 flex-1 flex-col")}>
            {expanded && (
                <div className="flex items-center justify-between gap-2 px-3 pb-1 text-[9px] font-semibold uppercase tracking-[0.14em] text-muted">
                    <span>{label}</span>
                    {meta}
                </div>
            )}
            <div className={cx("flex flex-col gap-0.5", fill && "min-h-0 flex-1", flush ? "px-0" : "px-1.5")}>{children}</div>
        </section>
    )
}

function SidebarTooltip({ label, enabled, children }: { label: string; enabled: boolean; children: ReactNode }): JSX.Element {
    if (!enabled) return <>{children}</>
    return (
        <Tooltip.Root delay={0}>
            <Tooltip.Trigger render={children as ReactElement} />
            <Tooltip.Portal>
                <Tooltip.Positioner side="left" sideOffset={8}>
                    <Tooltip.Popup className="z-50 border border-border bg-base-300 px-2 py-1 text-xs text-base-content shadow-lg">{label}</Tooltip.Popup>
                </Tooltip.Positioner>
            </Tooltip.Portal>
        </Tooltip.Root>
    )
}

const SidebarContent = observer(function SidebarContent({
    expanded,
    taskId,
    workspaceId,
    tray,
    taskModel,
    gitStatus,
    pullRequest,
    externalWatchStatus,
    pullRequestSnapshot,
    worktree,
    onDirectoryChange,
    onCheckoutSummariesOpen,
    showTestFiles = false,
    enabledMcpServerIds,
    onMcpServerIdsChange,
    onNavigate,
    onTimelineNavigate,
    hideWorkspaceTools,
    onToggleExpanded,
    onPullRequestRefresh,
    mobile = false,
}: Omit<TaskWorkspaceSidebarProps, "mobileOpen" | "onMobileClose"> & {
    expanded: boolean
    mobile?: boolean
    onNavigate?: () => void
    onToggleExpanded?: () => void
    onPullRequestRefresh?: () => Promise<string | null>
}) {
    const portalContainer = usePortalContainer()
    const codeStore = useOptionalCodeStore()
    const adminSession = codeStore?.isGuest ? null : taskModel.primarySessionThreadReference
    // Guests read; tools that write, execute, or open a shell stay hidden for them.
    const guestTrays = new Set(["gitlog", "files", "search", "sessions"])
    const visibleConfigs = TRAY_CONFIGS.filter(
        (config) =>
            (config.id === "sessions" || !hideWorkspaceTools) &&
            config.isVisible?.(tray) !== false &&
            (codeStore?.canOperate !== false || guestTrays.has(config.id))
    )
    const gitLogVisible = visibleConfigs.some((config) => config.id === "gitlog")
    const hasCheckoutSelector = Boolean(worktree && onDirectoryChange)
    const nestCheckoutTools = expanded && hasCheckoutSelector && worktree?.checkoutSummariesEnabled === true
    const workspaceTrayConfigs = visibleConfigs.filter((config) => config.id !== "sessions")
    const workspaceConfigs = !expanded && hasCheckoutSelector ? workspaceTrayConfigs.filter((config) => config.id !== "gitlog") : workspaceTrayConfigs
    const checkoutScopedConfigs = nestCheckoutTools ? workspaceConfigs.filter((config) => CHECKOUT_SCOPED_TRAYS.has(config.id)) : []
    const topLevelWorkspaceConfigs = nestCheckoutTools ? workspaceConfigs.filter((config) => !CHECKOUT_SCOPED_TRAYS.has(config.id)) : workspaceConfigs
    const auxiliarySessions = taskModel.sessionDebugInfo.filter((session) => session.role === "auxiliary" || Boolean(session.parentSessionId))
    const sessionsActive = tray.openTray === "sessions"
    const [directoryPickerPending, setDirectoryPickerPending] = useState(false)

    const toggleCheckoutGitLog = () => {
        if (gitLogVisible) tray.toggle("gitlog")
        onNavigate?.()
    }

    const chooseDirectory = async () => {
        if (!worktree || !onDirectoryChange || directoryPickerPending) return
        setDirectoryPickerPending(true)
        try {
            const path = await selectDirectory(worktree.manualPath ?? worktree.path)
            if (!path) return
            const error = await onDirectoryChange(path)
            if (error) {
                console.warn("[TaskWorkspaceSidebar] Failed to select checkout:", error)
                return
            }
        } catch (error) {
            console.error("[TaskWorkspaceSidebar] Failed to select checkout:", error)
        } finally {
            setDirectoryPickerPending(false)
        }
    }

    const renderPullRequestControl = () => {
        if (!pullRequest) return null
        return (
            <LinkedRequestControl
                key={`${taskId}:${pullRequest.url}`}
                pullRequest={pullRequest}
                snapshot={pullRequestSnapshot}
                watchStatus={externalWatchStatus}
                automation={taskModel.pullRequestAutomation}
                automationDisabledReason={taskModel.input.pullRequestAutomationDisabledReason}
                onWatchChange={(enabled) => taskModel.setWatchPR(enabled)}
                onRetryAutomation={() => taskModel.retryPullRequestAutomation()}
                onRefresh={onPullRequestRefresh}
                onRemove={taskModel.pullRequestAutomation ? undefined : () => taskModel.unlinkPullRequest()}
            />
        )
    }

    const renderWorkspaceButtons = (configs: TrayConfig[], nested = false) => (
        <div className={cx("flex flex-col gap-0.5", nested ? "py-1" : expanded && hasCheckoutSelector && "px-1.5 pt-1")}>
            {configs.map((config) => {
                const Icon = config.icon
                const badge = config.renderBadge?.(tray)
                const status = config.renderButtonStatus?.(tray)
                const label = config.getButtonLabel?.(tray) ?? config.label
                const displayLabel = nested && config.id === "gitlog" ? "Changes" : config.label
                const active = tray.isTrayOpen(config.id)
                const button = (
                    <button
                        key={config.id}
                        type="button"
                        onClick={() => {
                            tray.toggle(config.id)
                            onNavigate?.()
                        }}
                        className={cx(
                            "btn relative flex min-h-9 w-full items-center text-xs transition-colors",
                            expanded ? "gap-2 px-2 text-left" : "justify-center px-0",
                            active ? "bg-primary/15 text-primary" : "text-muted hover:bg-base-200 hover:text-base-content"
                        )}
                        aria-label={label}
                        title={expanded ? label : undefined}
                        aria-pressed={active}
                    >
                        <Icon size={15} className="shrink-0" />
                        {expanded && (
                            <>
                                <span className="min-w-0 flex-1 truncate font-medium text-base-content">{displayLabel}</span>
                                {status !== null && status !== undefined && <span className="shrink-0">{status}</span>}
                            </>
                        )}
                        {badge !== null && badge !== undefined && (
                            <span className={cx("shrink-0", !expanded && "absolute -right-0.5 -top-0.5")}>
                                <TrayBadge>{badge}</TrayBadge>
                            </span>
                        )}
                    </button>
                )
                return (
                    <Fragment key={config.id}>
                        <SidebarTooltip label={label} enabled={!expanded}>
                            {button}
                        </SidebarTooltip>
                        {config.id === "scratchpad" && (
                            <SidebarTooltip label="Processes · all computers" enabled={!expanded}>
                                <button
                                    type="button"
                                    className={cx(
                                        "btn relative flex min-h-9 w-full items-center text-xs text-muted transition-colors hover:bg-base-200 hover:text-base-content",
                                        expanded ? "gap-2 px-2 text-left" : "justify-center px-0"
                                    )}
                                    aria-label="Processes"
                                    aria-haspopup="dialog"
                                    title={expanded ? "Processes on all computers" : undefined}
                                    onClick={() => {
                                        const nodeId = nestClient.getDiagnostics().nodeId
                                        void NiceModal.show(ProcessesModal, { currentTask: nodeId ? { taskId, nodeId } : undefined })
                                        onNavigate?.()
                                    }}
                                >
                                    <Server size={15} className="shrink-0" />
                                    {expanded && <span className="min-w-0 flex-1 truncate font-medium text-base-content">Processes</span>}
                                </button>
                            </SidebarTooltip>
                        )}
                    </Fragment>
                )
            })}
        </div>
    )

    if (tray.openTray && !visibleConfigs.some((config) => config.id === tray.openTray)) tray.close()

    return (
        <>
            <section className={cx("border-b border-border", expanded ? "flex h-11 items-center gap-0.5 px-2" : "flex flex-col gap-0.5 px-1.5 py-1.5")}>
                {!expanded && onToggleExpanded && (
                    <SidebarTooltip label="Expand task workspace" enabled>
                        <button
                            type="button"
                            className="btn flex min-h-9 w-full items-center justify-center text-muted hover:bg-base-200 hover:text-base-content"
                            onClick={onToggleExpanded}
                            aria-label="Expand task workspace"
                            title="Expand task workspace"
                        >
                            <PanelRightOpen size={15} />
                        </button>
                    </SidebarTooltip>
                )}
                {auxiliarySessions.length > 0 && (
                    <SidebarTooltip label="Sessions" enabled={!expanded}>
                        <button
                            type="button"
                            className={cx(
                                "btn flex items-center hover:bg-base-200 hover:text-base-content",
                                sessionsActive ? "bg-primary/15 text-primary" : "text-muted",
                                expanded ? "h-7 w-7 justify-center px-0" : "min-h-9 w-full justify-center px-0"
                            )}
                            onClick={() => {
                                tray.toggle("sessions")
                                onNavigate?.()
                            }}
                            aria-label={`Sessions, ${auxiliarySessions.length} auxiliary`}
                            aria-pressed={sessionsActive}
                        >
                            <GitFork size={14} />
                        </button>
                    </SidebarTooltip>
                )}
                <SidebarTooltip label="Export conversation" enabled={!expanded}>
                    <div className={cx(!expanded && "flex justify-center")}>
                        <CopyMarkdownButton taskId={taskId} />
                    </div>
                </SidebarTooltip>
                <SidebarTooltip label="Task details" enabled={!expanded}>
                    <div className={cx(!expanded && "flex justify-center")}>
                        <TaskSessionInfoButton
                            side={mobile ? "top" : "left"}
                            showDirectorySelector={false}
                            worktree={worktree}
                            onOpen={() => {
                                void taskModel.loadTaskSessionManifest()
                            }}
                            details={{
                                taskId,
                                title: taskModel.title,
                                workspaceId: taskModel.workspaceId,
                                status: taskModel.isWorking
                                    ? "Running"
                                    : taskModel.pullRequestAutomation
                                      ? pullRequestAutomationStatus(taskModel.pullRequestAutomation)
                                      : taskModel.isClosed
                                        ? "Closed"
                                        : "Idle",
                                createdAt: taskModel.createdAt,
                                usage: taskModel.stats,
                                sessions: taskModel.sessionDebugInfo,
                                context: taskModel.taskSessionManifest,
                                contextLoading: taskModel.taskSessionManifestLoading,
                                contextError: taskModel.taskSessionManifestError,
                            }}
                        />
                    </div>
                </SidebarTooltip>
                {expanded && onToggleExpanded && (
                    <SidebarTooltip label="Collapse task workspace" enabled={false}>
                        <button
                            type="button"
                            className="btn ml-auto flex h-7 w-7 shrink-0 items-center justify-center text-muted hover:bg-base-200 hover:text-base-content"
                            onClick={onToggleExpanded}
                            aria-label="Collapse task workspace"
                            title="Collapse task workspace"
                        >
                            <PanelRightClose size={15} />
                        </button>
                    </SidebarTooltip>
                )}
            </section>

            {(workspaceTrayConfigs.length > 0 || worktree || pullRequest || (enabledMcpServerIds && onMcpServerIdsChange) || adminSession) && (
                <SidebarSection
                    label="Workspace"
                    expanded={expanded}
                    flush={expanded && hasCheckoutSelector}
                    meta={
                        worktree && (
                            <span className="flex items-center gap-2 font-mono font-normal normal-case tracking-normal">
                                <span>vs {worktree.checkoutSummaryBaseBranch ?? "base"}</span>
                                {worktree?.checkoutSummaryReads && worktree.checkoutSummaryReads.failed > 0 && (
                                    <span
                                        className="flex items-center gap-0.5 text-warning"
                                        title={`Checkout summaries: ${worktree.checkoutSummaryReads.succeeded}/${worktree.checkoutSummaryReads.requested} loaded\n${worktree.checkoutSummaryReads.firstErrors.join("\n")}`}
                                    >
                                        <CircleAlert size={10} />
                                        {worktree.checkoutSummaryReads.succeeded}/{worktree.checkoutSummaryReads.requested}
                                    </span>
                                )}
                                {onDirectoryChange && (
                                    <button
                                        type="button"
                                        className="btn flex h-5 w-5 items-center justify-center text-muted hover:bg-base-200 hover:text-base-content disabled:opacity-50"
                                        onClick={() => void chooseDirectory()}
                                        disabled={directoryPickerPending}
                                        aria-label="Choose directory"
                                        title="Add or choose a checkout"
                                    >
                                        {directoryPickerPending ? <Loader2 size={11} className="animate-spin" /> : <Plus size={12} />}
                                    </button>
                                )}
                            </span>
                        )
                    }
                >
                    {hasCheckoutSelector &&
                        (expanded ? (
                            <TaskDirectorySelector
                                worktree={worktree}
                                onDirectoryChange={onDirectoryChange}
                                excludeTestFiles={!showTestFiles}
                                narrow
                                renderActiveDirectoryContent={
                                    checkoutScopedConfigs.length > 0
                                        ? () => <div data-task-directory-tools="true">{renderWorkspaceButtons(checkoutScopedConfigs, true)}</div>
                                        : undefined
                                }
                            />
                        ) : (
                            <div className="flex justify-center">
                                <TaskGitControl
                                    gitStatus={gitStatus}
                                    worktree={worktree}
                                    onDirectoryChange={onDirectoryChange}
                                    onCheckoutSummariesOpen={onCheckoutSummariesOpen}
                                    onDirectoryActivated={toggleCheckoutGitLog}
                                    onChooseDirectory={() => void chooseDirectory()}
                                    directoryPickerPending={directoryPickerPending}
                                    excludeTestFiles={!showTestFiles}
                                    touchFriendly={false}
                                    iconOnly
                                    side={mobile ? "top" : "left"}
                                    align="end"
                                />
                            </div>
                        ))}
                    {pullRequest && (
                        <div
                            role="group"
                            aria-label="Pull request"
                            className={cx("border-b border-border pb-1", expanded ? "px-1.5" : "flex justify-center", hasCheckoutSelector && "pt-1")}
                        >
                            {expanded ? (
                                renderPullRequestControl()
                            ) : (
                                <Popover.Root>
                                    <Popover.Trigger
                                        className={cx(
                                            "btn flex min-h-9 w-full items-center justify-center hover:bg-base-200",
                                            pullRequestSnapshot?.state === "closed" ||
                                                pullRequestSnapshot?.checks === "failing" ||
                                                externalWatchStatus === "degraded"
                                                ? "text-warning"
                                                : "text-primary"
                                        )}
                                        aria-label={`Pull request${pullRequest.number ? ` #${pullRequest.number}` : ""}`}
                                    >
                                        {externalWatchStatus === "degraded" ? <CircleAlert size={15} /> : <GitPullRequest size={15} />}
                                    </Popover.Trigger>
                                    <Popover.Portal container={portalContainer}>
                                        <Popover.Positioner side={mobile ? "top" : "left"} align="end" sideOffset={8} collisionPadding={8} className="z-50">
                                            <Popover.Popup className="w-72 border border-border bg-base-100 p-1.5 shadow-lg outline-none">
                                                {renderPullRequestControl()}
                                            </Popover.Popup>
                                        </Popover.Positioner>
                                    </Popover.Portal>
                                </Popover.Root>
                            )}
                        </div>
                    )}
                    {renderWorkspaceButtons(topLevelWorkspaceConfigs)}
                    {((enabledMcpServerIds && onMcpServerIdsChange) || adminSession) && (
                        <div className={cx("flex flex-col gap-0.5", expanded && hasCheckoutSelector && "px-1.5")}>
                            {enabledMcpServerIds && onMcpServerIdsChange && (
                                <Popover.Root>
                                    <Popover.Trigger
                                        className={cx(
                                            "btn flex min-h-9 w-full items-center text-xs transition-colors hover:bg-base-200",
                                            expanded ? "gap-2 px-2 text-left" : "justify-center px-0",
                                            enabledMcpServerIds.length > 0 ? "text-primary" : "text-muted"
                                        )}
                                        aria-label={`Connections${enabledMcpServerIds.length > 0 ? `, ${enabledMcpServerIds.length} enabled` : ""}`}
                                    >
                                        <Plug size={15} className="shrink-0" />
                                        {expanded && <span className="min-w-0 flex-1 truncate font-medium text-base-content">Connections</span>}
                                        {enabledMcpServerIds.length > 0 && <TrayBadge>{enabledMcpServerIds.length}</TrayBadge>}
                                    </Popover.Trigger>
                                    <Popover.Portal container={portalContainer}>
                                        <Popover.Positioner side={mobile ? "top" : "left"} align="end" sideOffset={8} collisionPadding={8} className="z-50">
                                            <Popover.Popup className="max-h-[min(32rem,80dvh)] overflow-y-auto border border-border bg-base-100 p-3 shadow-lg outline-none">
                                                <TaskMcpSelector selectedServerIds={enabledMcpServerIds} onSelectionChange={onMcpServerIdsChange} vertical />
                                            </Popover.Popup>
                                        </Popover.Positioner>
                                    </Popover.Portal>
                                </Popover.Root>
                            )}
                            {adminSession &&
                                (expanded ? (
                                    <AppToolsControl identity={{ taskId, workspaceId, sessionId: adminSession.sessionId }} compact />
                                ) : (
                                    <Popover.Root>
                                        <Popover.Trigger
                                            className="btn flex min-h-9 w-full items-center justify-center text-muted hover:bg-base-200 hover:text-base-content"
                                            aria-label="Admin capabilities"
                                            title="Admin capabilities"
                                        >
                                            <ShieldCheck size={15} aria-hidden="true" />
                                        </Popover.Trigger>
                                        <Popover.Portal container={portalContainer}>
                                            <Popover.Positioner side="left" align="end" sideOffset={8} collisionPadding={8} className="z-50">
                                                <Popover.Popup className="w-64 border border-border bg-base-100 p-2 shadow-lg outline-none">
                                                    <AppToolsControl identity={{ taskId, workspaceId, sessionId: adminSession.sessionId }} compact />
                                                </Popover.Popup>
                                            </Popover.Positioner>
                                        </Popover.Portal>
                                    </Popover.Root>
                                ))}
                        </div>
                    )}
                </SidebarSection>
            )}

            <SidebarSection label="Activity" expanded={expanded} flush={!expanded} fill>
                <TaskTimelineRail
                    taskId={taskId}
                    workspaceId={workspaceId}
                    expanded={expanded}
                    onNavigate={
                        onTimelineNavigate
                            ? (target) => {
                                  onTimelineNavigate(target)
                                  onNavigate?.()
                              }
                            : undefined
                    }
                />
            </SidebarSection>
        </>
    )
})

export const TaskWorkspaceSidebar = observer(function TaskWorkspaceSidebar(props: TaskWorkspaceSidebarProps) {
    const [expanded, setExpanded] = useState(true)
    const [externalWatchStatus, setExternalWatchStatus] = useState<WildLarkExternalWatchStatusResult["status"] | undefined>()
    const [pullRequestSnapshot, setPullRequestSnapshot] = useState<WildLarkPullRequestSnapshot | undefined>()
    const mobile = useMediaQuery("(max-width: 720px)")

    const refreshPullRequest = async (): Promise<string | null> => {
        if (props.pullRequest?.watch !== "active") return "Live state is unavailable for this request."
        const sessionId = props.taskModel.primarySessionThreadReference?.sessionId
        if (!sessionId) return "This task does not have a resumable session."
        const [watchResult, pullRequestResult] = await Promise.allSettled([
            requestWildLarkMethod("wildlark.externalWatch.status", {}),
            requestWildLarkMethod("wildlark.task.pr.read", {
                taskId: props.taskId,
                workspaceId: props.workspaceId,
                sessionId,
            }),
        ])
        if (watchResult.status === "fulfilled") setExternalWatchStatus(watchResult.value.status)
        else {
            console.warn("[TaskWorkspaceSidebar] Failed to read external watch status:", watchResult.reason)
            setExternalWatchStatus(undefined)
        }
        if (pullRequestResult.status === "fulfilled") {
            setPullRequestSnapshot(pullRequestResult.value.snapshot)
        } else {
            console.warn("[TaskWorkspaceSidebar] Failed to read pull request state:", pullRequestResult.reason)
            setPullRequestSnapshot(undefined)
        }
        if (pullRequestResult.status === "rejected") {
            return pullRequestResult.reason instanceof Error ? pullRequestResult.reason.message : "The pull request state could not be refreshed."
        }
        return null
    }

    useEffect(() => {
        if (props.pullRequest?.watch !== "active") {
            setExternalWatchStatus(undefined)
            setPullRequestSnapshot(undefined)
            return
        }
        const sessionId = props.taskModel.primarySessionThreadReference?.sessionId
        if (!sessionId) return
        let cancelled = false
        const load = async () => {
            const [watchResult, pullRequestResult] = await Promise.allSettled([
                requestWildLarkMethod("wildlark.externalWatch.status", {}),
                requestWildLarkMethod("wildlark.task.pr.read", {
                    taskId: props.taskId,
                    workspaceId: props.workspaceId,
                    sessionId,
                }),
            ])
            if (!cancelled) {
                if (watchResult.status === "fulfilled") setExternalWatchStatus(watchResult.value.status)
                else {
                    console.warn("[TaskWorkspaceSidebar] Failed to read external watch status:", watchResult.reason)
                    setExternalWatchStatus(undefined)
                }
                if (pullRequestResult.status === "fulfilled") setPullRequestSnapshot(pullRequestResult.value.snapshot)
                else {
                    console.warn("[TaskWorkspaceSidebar] Failed to read pull request state:", pullRequestResult.reason)
                    setPullRequestSnapshot(undefined)
                }
            }
        }
        void load()
        return () => {
            cancelled = true
        }
    }, [
        props.pullRequest?.id,
        props.pullRequest?.watch,
        props.taskId,
        props.taskModel.primarySessionThreadReference?.sessionId,
        props.taskModel.pullRequestAutomation?.phase,
        props.taskModel.pullRequestAutomation?.hold?.reason,
        props.workspaceId,
    ])

    useEffect(() => {
        if ((!mobile && expanded) || (mobile && props.mobileOpen)) props.onCheckoutSummariesOpen?.()
    }, [expanded, mobile, props.mobileOpen, props.onCheckoutSummariesOpen])

    useEffect(() => {
        props.onExpandedChange?.(expanded)
    }, [expanded, props.onExpandedChange])

    return (
        <>
            {!mobile && (
                <aside
                    className={cx(
                        "relative flex min-h-0 shrink-0 flex-col border-l border-border bg-base-100 transition-[width] duration-150",
                        props.desktopContained ? "h-full w-full" : expanded ? "w-72" : "w-12"
                    )}
                    style={{ zIndex: Z_INDEX.TASK_WORKSPACE_SIDEBAR }}
                    data-task-workspace-sidebar="true"
                    data-expanded={expanded}
                    aria-label="Task workspace"
                    onDoubleClick={(event) => {
                        if (expanded) return
                        const target = event.target instanceof Element ? event.target : null
                        if (target?.closest("button, a, input, [role='button'], [data-task-timeline-marker='true']")) return
                        setExpanded(true)
                    }}
                    title={expanded ? undefined : "Double-click to expand task workspace"}
                >
                    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain">
                        <SidebarContent
                            {...props}
                            externalWatchStatus={externalWatchStatus?.state}
                            pullRequestSnapshot={pullRequestSnapshot}
                            onPullRequestRefresh={refreshPullRequest}
                            expanded={expanded}
                            onToggleExpanded={() => setExpanded((value) => !value)}
                        />
                    </div>
                </aside>
            )}

            {mobile && props.mobileOpen && (
                <CompactSheet title="Task tools" onClose={props.onMobileClose}>
                    <SidebarContent
                        {...props}
                        externalWatchStatus={externalWatchStatus?.state}
                        pullRequestSnapshot={pullRequestSnapshot}
                        onPullRequestRefresh={refreshPullRequest}
                        expanded
                        mobile
                        onNavigate={props.onMobileClose}
                    />
                </CompactSheet>
            )}
        </>
    )
})
